<template>
    <el-form :model="form"
             ref="form"
             size="mini"
             :rules="rulesForm"
             hide-required-asterisk
             :show-message="false">
        <div class="col-form-items">
            <el-form-item class="form-item" prop="moduleNormal" :label-width="labelWidth.col1">
                <span slot="label">Module<sub>n</sub></span>
                <el-input v-model="form.moduleNormal" class="form-field" @change="changeTooth">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
            <el-form-item class="form-item" prop="pressureAngleNormal" :label-width="labelWidth.col2">
                <span slot="label">Pressure angle<sub>n</sub></span>
                <el-input v-model="form.pressureAngleNormal" class="form-field" @change="changeTooth">
                    <template slot="suffix">{{units.angle}}</template>
                </el-input>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Helix angle" prop="helixAngle" :label-width="labelWidth.col1">
                <el-input v-model="form.helixAngle" class="form-field" @change="changeTooth">
                    <template slot="suffix">{{units.angle}}</template>
                </el-input>
            </el-form-item>
            <el-form-item class="form-item" label="Number of teeth" prop="numberOfTeeth" :label-width="labelWidth.col2">
                <el-input v-model="form.numberOfTeeth" class="form-field" @change="changeTooth"/>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Profile shift coeff." prop="profileShiftCoefficient" :label-width="labelWidth.col1">
                <el-input v-model="form.profileShiftCoefficient" class="form-field" @change="changeTooth" :disabled="!cutWorkflow"/>
            </el-form-item>
            <el-form-item class="form-item" prop="toothThicknessNormal" :label-width="labelWidth.col2">
                <span slot="label">
                    <el-tooltip content="Normal tooth thickness resulting from the cutting process."
                                placement="top-start" :tabindex="-1" :open-delay="$defaults.tooltipDelay">
                        <span>Pre-finished tt<sub>n</sub><small><i :class="$icons.chatBox"/></small></span>
                    </el-tooltip>
                </span>
                <el-input v-model="form.toothThicknessNormal" class="form-field" @change="changeTooth" :disabled="cutWorkflow">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Root diameter" prop="rootDiameter" :label-width="labelWidth.col1">
                <el-input v-model="form.rootDiameter" class="form-field" @change="changeTooth" :disabled="cutWorkflow">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
            <el-form-item class="form-item" label="Tip diameter" prop="tipDiameter" :label-width="labelWidth.col2">
                <el-input v-model="form.tipDiameter" class="form-field" @change="changeTooth" :disabled="cutWorkflow">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" prop="rimDiameter" :label-width="labelWidth.col1">
                <span v-if="internal" slot="label">Rim diameter</span>
                <span v-else slot="label">Bore diameter</span>
                <el-input v-model="form.rimDiameter" class="form-field" @change="changeTooth">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
            <el-form-item class="form-item" label="Work piece dia." prop="workPieceDiameter" :label-width="labelWidth.col2">
                <el-input v-model="form.workPieceDiameter" class="form-field" @change="changeTooth" :disabled="!cutWorkflow">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Facewidth" prop="facewidth" :label-width="labelWidth.col1">
                <el-input v-model="form.facewidth" class="form-field" @change="changeTooth">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
        </div>

        <!--CUTTER DATA - no *simple* way to avoid specifying tool data-->
        <div v-if="isGeneratingRack" class="section-heading">
            <el-button @click="$tabs.hob()" type="text" class="cutter-btn">
                <dh-icon :name="$icons.toolHob"/>
                <h5>Cutter Data</h5>
            </el-button>
            <hr>
            <el-checkbox v-model="formGeneratingRack.modifiedLead" class="heading-btn" @change="changeRackLead">Short / Long Lead</el-checkbox>
        </div>
        <div v-else-if="isGeneratingPinion" class="section-heading">
            <el-button @click="$tabs.shaper()" type="text" class="cutter-btn">
                <dh-icon :name="$icons.toolShaper"/>
                <h5>Cutter Data</h5>
            </el-button>
            <hr>
            <el-checkbox v-model="formGeneratingPinion.modifiedLead" class="heading-btn" @change="changePinionLead">Short / Long Lead</el-checkbox>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Cutting method" prop="cutType" :label-width="labelWidth.col1" style="justify-self: right">
                <el-select v-model="form.cutType" automatic-dropdown class="form-field" @change="emitChangeCutType">
                    <el-option
                            v-for="(item, index) in cutTypeOptions"
                            :key="index"
                            :label="$options.cutTypeLabels[item]"
                            :value="item">
                    </el-option>
                </el-select>
            </el-form-item>
            <el-form-item class="form-item" label="Get tool data" prop="tool" :label-width="labelWidth.col2">
                <el-dropdown size="medium" @command="handleSelectTool">
                    <el-button size="small" :disabled="!form.cutType">
                        <i class="el-icon-edit-outline"/> Select tool <i class="el-icon-arrow-down"/>
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item v-for="item in toolOptions" :key="item" :command="item">
                            {{ item }}
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </el-form-item>
        </div>
        <el-form v-if="isGeneratingRack"
                 :model="formGeneratingRack"
                 ref="formGeneratingRack"
                 size="mini"
                 :rules="$options.rulesGeneratingRack"
                 hide-required-asterisk
                 :show-message="false">
            <div class="col-form-items">
                <el-form-item class="form-item" prop="pressureAngleNormal" :label-width="labelWidth.col1">
                        <span slot="label">
                            <el-tooltip content="Generating tool module and pressure angle can differ from the reference tooth by use of short or long lead tooling."
                                        placement="top-start" :tabindex="-1" :open-delay="$defaults.tooltipDelay">
                                <span>Pressure angle<sub>n</sub><small><i :class="$icons.chatBox"/></small></span>
                            </el-tooltip>
                        </span>
                    <el-input v-model="formGeneratingRack.pressureAngleNormal" class="form-field" @change="changeRack" :disabled="!formGeneratingRack.modifiedLead">
                        <template slot="suffix">{{units.angle}}</template>
                    </el-input>
                </el-form-item>
            </div>
            <div class="col-form-items">
                <el-form-item class="form-item" label="Addendum coeff." prop="addendumCoefficient" :label-width="labelWidth.col1">
                    <el-input v-model="formGeneratingRack.addendumCoefficient" class="form-field" @change="changeRack" :disabled="!cutWorkflow"/>
                </el-form-item>
                <el-form-item class="form-item" label="Dedendum coeff." prop="dedendumCoefficient" :label-width="labelWidth.col2">
                    <el-input v-model="formGeneratingRack.dedendumCoefficient" class="form-field" @change="changeRack" :disabled="!cutWorkflow"/>
                </el-form-item>
            </div>
            <div class="col-form-items">
                <el-form-item class="form-item" prop="protuberanceSize" :label-width="labelWidth.col1">
                    <span slot="label">Protuberance size<sub>n</sub></span>
                    <el-input v-model="formGeneratingRack.protuberanceSize" class="form-field" @change="changeRack">
                        <template slot="suffix">mm</template>
                    </el-input>
                </el-form-item>
                <el-form-item class="form-item" prop="protuberanceAngle" :label-width="labelWidth.col2">
                    <span slot="label">Protuberance ang<sub>n</sub></span>
                    <el-input v-model="formGeneratingRack.protuberanceAngle" class="form-field" @change="changeRack">
                        <template slot="suffix">{{units.angle}}</template>
                    </el-input>
                </el-form-item>
            </div>
            <div class="col-form-items">
                <el-form-item class="form-item" label="Tip radius coeff." prop="tipRadiusCoefficient" :label-width="labelWidth.col1">
                    <el-input v-model="formGeneratingRack.tipRadiusCoefficient" class="form-field" @change="changeRack" :disabled="formGeneratingRack.tipRadiusMax"/>
                </el-form-item>
                <el-form-item class="form-item" label="Max tip radius" prop="tipRadiusMax" :label-width="labelWidth.col2">
                    <el-checkbox v-model="formGeneratingRack.tipRadiusMax" class="form-field" @change="changeRackTip"/>
                </el-form-item>
                <!--region ALTERNATIVE: Dimensionless tool tip size.-->
                <!--<el-form-item class="form-item" label="Tool tip radius" prop="toolTipSize" :label-width="labelWidth.col1">-->
                <!--&lt;!&ndash; Unit-less slider to control root fillet size &ndash;&gt;-->
                <!--<el-slider class="form-field"-->
                <!--id="root-fillet-slider"-->
                <!--v-model="formGeneratingRack.toolTipSize"-->
                <!--:format-tooltip="formatSlider"-->
                <!--:step="10"-->
                <!--@change="emitValidate"/>-->
                <!--</el-form-item>-->
                <!--endregion-->
            </div>
        </el-form>
        <el-form v-else-if="isGeneratingPinion"
                 :model="formGeneratingPinion"
                 ref="formGeneratingPinion"
                 size="mini"
                 :rules="$options.rulesGeneratingPinion"
                 hide-required-asterisk
                 :show-message="false">
            <div class="col-form-items">
                <el-form-item class="form-item" prop="pressureAngleNormal" :label-width="labelWidth.col1">
                    <span slot="label">
                        <el-tooltip content="Generating tool module and pressure angle can differ from the reference tooth by use of short or long lead tooling."
                                    placement="top-start" :tabindex="-1" :open-delay="$defaults.tooltipDelay">
                            <span>Pressure angle<sub>n</sub><small><i :class="$icons.chatBox"/></small></span>
                        </el-tooltip>
                    </span>
                    <el-input v-model="formGeneratingPinion.pressureAngleNormal" class="form-field" @change="changePinion" :disabled="!formGeneratingPinion.modifiedLead">
                        <template slot="suffix">{{units.angle}}</template>
                    </el-input>
                </el-form-item>
                <el-form-item class="form-item" label="Number of teeth" prop="numberOfTeeth" :label-width="labelWidth.col2">
                    <el-input v-model="formGeneratingPinion.numberOfTeeth" class="form-field" @change="changePinion"/>
                </el-form-item>
            </div>
            <div class="col-form-items">
                <el-form-item class="form-item" label="Addendum coeff." prop="addendumCoefficient" :label-width="labelWidth.col1">
                    <el-input v-model="formGeneratingPinion.addendumCoefficient" class="form-field" @change="changePinion" :disabled="!cutWorkflow"/>
                </el-form-item>
                <el-form-item class="form-item" label="Dedendum coeff." prop="dedendumCoefficient" :label-width="labelWidth.col2">
                    <el-input v-model="formGeneratingPinion.dedendumCoefficient" class="form-field" @change="changePinion" :disabled="!cutWorkflow"/>
                </el-form-item>
            </div>
            <div class="col-form-items">
                <el-form-item class="form-item" prop="profileShiftCoefficient" :label-width="labelWidth.col1">
                    <span slot="label">
                        <el-tooltip content="Only modifies cutter tooth thickness, which changes as shapers becomes worn."
                                    placement="top-start" :tabindex="-1" :open-delay="$defaults.tooltipDelay">
                            <span>Profile shift coeff.<small><i :class="$icons.chatBox"/></small></span>
                        </el-tooltip>
                    </span>
                    <el-input v-model="formGeneratingPinion.profileShiftCoefficient" class="form-field" @change="changePinion"/>
                </el-form-item>
                <el-form-item class="form-item" prop="protuberanceSize" :label-width="labelWidth.col2">
                    <span slot="label">Protuberance size<sub>n</sub></span>
                    <el-input v-model="formGeneratingPinion.protuberanceSize" class="form-field" @change="changePinion" :disabled="internal">
                        <template slot="suffix">mm</template>
                    </el-input>
                </el-form-item>
            </div>
            <div class="col-form-items">
                <el-form-item class="form-item" label="Tip radius coeff." prop="tipRadiusCoefficient" :label-width="labelWidth.col1">
                    <el-input v-model="formGeneratingPinion.tipRadiusCoefficient" class="form-field"
                              @change="changePinion" :disabled="formGeneratingPinion.tipRadiusMax"/>
                </el-form-item>
                <el-form-item class="form-item" label="Max tip radius" prop="tipRadiusMax" :label-width="labelWidth.col2">
                    <el-checkbox v-model="formGeneratingPinion.tipRadiusMax" class="form-field" @change="changePinionTip"/>
                </el-form-item>
            </div>
        </el-form>
    </el-form>
</template>

<script>
  import {rules} from "../../assets/js/validators";
  import {EventBus} from "../../event_bus";

  // helps prevent messages from being stacked on each other
  const delayMessage = (callback) => setTimeout(callback, 20);

  export default {
    name: "FormGearDetailTooth",
    components: {},
    props: {
      form: Object,
      formGeneratingRack: Object,
      formGeneratingPinion: Object,
      // WORKFLOW
      internal: Boolean,
      cutWorkflow: Boolean,
      // CUTTER
      cutTypeOptions: Array,
      isGeneratingRack: Boolean,
      isGeneratingPinion: Boolean,
      // FORMAT
      labelWidth: Object,
      units: Object,
      // METHODS
      updateGeomFields: Function
    },

    // STATIC DATA
    tool: {
      hob: 'Hobbing Tool',
      shaper: 'Shaping Tool',
      basicRack: 'Basic Rack',
    },
    cutTypeLabels: {
      GENERATING_RACK: 'Generating rack',
      GENERATING_PINION: 'Generating pinion',
    },
    rulesGeneratingRack: {
      pressureAngleNormal: rules.requiredPositiveNumber,
      addendumCoefficient: rules.requiredPositiveNumber,
      dedendumCoefficient: rules.requiredPositiveNumber,
      protuberanceSize: rules.requiredZeroPositiveNumber,
      protuberanceAngle: rules.requiredZeroPositiveNumber,
      tipRadiusCoefficient: rules.requiredZeroPositiveNumber,
    },
    rulesGeneratingPinion: {
      pressureAngleNormal: rules.requiredPositiveNumber,
      numberOfTeeth: rules.requiredPositiveInteger,
      addendumCoefficient: rules.requiredPositiveNumber,
      dedendumCoefficient: rules.requiredPositiveNumber,
      profileShiftCoefficient: rules.requiredNumber,  // +/- allowed
      protuberanceSize: rules.requiredZeroPositiveNumber,
      tipRadiusCoefficient: rules.requiredZeroPositiveNumber,
    },

    data() {
      // CUSTOM VALIDATORS
      const validateRimDiameter = (rule, value, callback) => {
        // PLACEHOLDER {UX} only allow $messages to be called once within X milliseconds

        let internalFactor, eMsg;
        if (this.internal) {
          internalFactor = -1;
          eMsg = 'Rim diameter less than root diameter';
        } else {
          internalFactor = 1;
          eMsg = 'Bore diameter greater than root diameter';
        }

        // validate rim diameter < root diameter
        const rimDiameter = internalFactor * Number.parseFloat(value);
        const rootDiameter = internalFactor * Number.parseFloat(this.form.rootDiameter);

        if (rimDiameter >= rootDiameter) {
          callback(new Error(eMsg));
          delayMessage(() => {
            this.$message({
              type: 'error',
              message: eMsg,
              center: true,
            });
          })
        } else {
          callback();  // valid
        }
      };
      const validateWorkPieceDiameter = (rule, value, callback) => {
        // PLACEHOLDER {UX} only allow $messages to be called once within X milliseconds

        // validate work piece diameter > root diameter; technically should be form diameter
        const internalFactor = this.internal ? -1 : 1;
        const workPieceDiameter = internalFactor * Number.parseFloat(value);
        const rootDiameter = internalFactor * Number.parseFloat(this.form.rootDiameter);
        const workPieceLessThanRoot = workPieceDiameter <= rootDiameter;
        let rootMsg = 'Work piece outer diameter is less than gear root diameter';

        // validate cutter can fit inside internal work piece
        let cutterMsg, cutterCannotFit = false;
        if (this.internal) {
          const cutterTipDiameter = Number.parseFloat(this.formGeneratingPinion.tipDiameter);
          cutterCannotFit = cutterTipDiameter >= Math.abs(workPieceDiameter);
          cutterMsg = 'Cutter cannot sufficiently fit within work piece inner diameter';
          rootMsg = 'Work piece inner diameter is greater than gear root diameter';
        }

        // validation and user message
        if (workPieceLessThanRoot) {
          callback(new Error(rootMsg));
          delayMessage(() => {
            this.$message({
              type: 'error',
              message: rootMsg,
              center: true,
            });
          });
        } else if (cutterCannotFit) {
          callback(new Error(cutterMsg));
          delayMessage(() => {
            this.$message({
              type: 'error',
              message: cutterMsg,
              center: true,
            });
          })
        } else {
          callback();  // valid
        }
      };
      return {
        rulesForm: {
          moduleNormal: rules.requiredPositiveNumber,
          pressureAngleNormal: rules.requiredPositiveNumber,
          helixAngle: rules.requiredNumber,
          numberOfTeeth: rules.requiredPositiveInteger,
          profileShiftCoefficient: rules.requiredNumber,
          toothThicknessNormal: rules.requiredNumber,  // negative values possible for high profile shift
          rootDiameter: rules.requiredPositiveNumber,
          tipDiameter: rules.requiredPositiveNumber,
          rimDiameter: [].concat(rules.requiredZeroPositiveNumber, {validator: validateRimDiameter, trigger: ['blur']}),
          workPieceDiameter: [].concat(rules.requiredPositiveNumber, {validator: validateWorkPieceDiameter, trigger: ['blur']}),
          facewidth: rules.requiredPositiveNumber,
          cutType: [{required: true, message: 'Required', trigger: ['blur', 'change']}],
        },
      }
    },
    computed: {
      toolOptions() {
        switch (this.form.cutType) {
          case this.$requests.gear.CutType.GEN_RACK:
            return [this.$options.tool.basicRack, this.$options.tool.hob];
          case this.$requests.gear.CutType.GEN_PINION:
            // Excludes basic rack for these reasons:
            //  1. Shaper teeth have their own profile shift, i.e. tooth thickness, which varies with cutter wear.
            //  2. Shaper, as defined by Gears App, does not have a protuberance angle, i.e. no dual-involute.
            return [this.$options.tool.shaper];
        }
      },
    },

    methods: {
      // FORM EVENTS
      emitChangeCutType(val) {
        this.$emit('change-cut-type', val);
      },
      emitChangeTooth(valid) {
        this.$emit('change-tooth-geom', valid);
      },
      emitChangeTool(valid) {
        this.$emit('change-tool-geom', valid);
      },
      async changeField(emit) {
        // Helper function for DRY.
        // Critical for UX that updateGeomFields is called before validation of form fields.
        // Critical that validation is done on entire form before emit event.  Avoids bad requests, 400.

        try {
          this.updateGeomFields();
          await this.validate();
          emit(true);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('Unable to compute geometry fields: ', e);
          emit(false)
        }
      },
      changeTooth() {
        // @change event triggers only if the input has changed when the box loses focus or the Enter key is pressed
        // NOTE: To include prop as arg, could do @change="(val) => handleChange(val, 'propName')"

        this.changeField(this.emitChangeTooth);
      },
      changeRack() {
        // @change event triggers only if the input has changed when the box loses focus or the Enter key is pressed
        // NOTE: To include prop as arg, could do @change="(val) => handleChange(val, 'propName')"

        this.changeField(this.emitChangeTool);
      },
      changePinion() {
        // @change event triggers only if the input has changed when the box loses focus or the Enter key is pressed
        // NOTE: To include prop as arg, could do @change="(val) => handleChange(val, 'propName')"

        this.changeField(this.emitChangeTool);
      },
      changeRackLead(modifiedLead) {
        // If modified lead is selected, then emit change event for rack tool.
        // arg: boolean to indicate if modifiedLoad is selected for generating rack.

        const isDifferent = this.formGeneratingRack.pressureAngleNormal !== this.form.pressureAngleNormal;
        if (!modifiedLead && isDifferent) this.changeRack();
      },
      changePinionLead(modifiedLead) {
        // If modified lead is selected, then emit change event for pinion tool.
        // arg: boolean to indicate if modifiedLead is selected for generating pinion.

        const isDifferent = this.formGeneratingPinion.pressureAngleNormal !== this.form.pressureAngleNormal;
        if (!modifiedLead && isDifferent) this.changePinion();
      },
      changeRackTip(val) {
        // If max tip radius is selected, then emit change event for rack tool.
        // arg: boolean to indicate if tipRadiusMax is selected for generating rack.

        if (val) this.changeRack();
      },
      changePinionTip(val) {
        // If max tip radius is selected, then emit change event for pinion tool.
        // arg: boolean to indicate if tipRadiusMax is selected for generating pinion.

        if (val) this.changePinion();
      },
      // TOOL DATA
      handleSelectTool(tool) {
        // Open dialog window of the selected tool.

        switch (tool) {
          case this.$options.tool.basicRack:
            this.selectBasicRack();
            break;
          case this.$options.tool.hob:
            this.selectHob();
            break;
          case this.$options.tool.shaper:
            this.selectShaper();
            break;
        }
      },
      selectBasicRack() {
        // Open dialog to select basic rack and listen for assignment event.
        // PLACEHOLDER {UX} consider preventing assignment if tool is incompatible with reference tooth data

        this.$store.commit('SELECT_BASIC_RACK');

        EventBus.$once('assign-basic-rack', basicRack => {
          // if specified, assign basic rack params to form fields
          if (basicRack) {
            const geom = basicRack.geom;
            this.formGeneratingRack.pressureAngleNormal = geom.pressureAngle;
            this.formGeneratingRack.tipRadiusCoefficient = geom.rootRadiusCoefficient;
            this.formGeneratingRack.tipRadiusMax = geom.tipRadiusMax;
            this.formGeneratingRack.protuberanceSize = geom.undercutSize;
            this.formGeneratingRack.protuberanceAngle = geom.undercutAngle;
            this.formGeneratingRack.addendumCoefficient = geom.dedendumCoefficient;  // inverted
            this.formGeneratingRack.dedendumCoefficient = geom.addendumCoefficient;  // inverted

            // emit change event
            this.changeRack();
          }
        });
      },
      selectHob() {
        // Open dialog to select hob and listen for assignment event.
        // PLACEHOLDER {UX} consider preventing assignment if tool is incompatible with reference tooth data

        this.$store.commit('SELECT_HOB');

        EventBus.$once('assign-hob', hob => {
          // if specified, assign hob params to form fields
          if (hob) {
            const geom = hob.geom;
            this.formGeneratingRack.pressureAngleNormal = geom.pressureAngleNormal;
            this.formGeneratingRack.tipRadiusCoefficient = geom.tipRadiusCoefficient;
            this.formGeneratingRack.tipRadiusMax = geom.tipRadiusMax;
            this.formGeneratingRack.protuberanceSize = geom.protuberanceSize;
            this.formGeneratingRack.protuberanceAngle = geom.protuberanceAngle;
            this.formGeneratingRack.addendumCoefficient = geom.addendumCoefficient;
            this.formGeneratingRack.dedendumCoefficient = geom.dedendumCoefficient;

            // emit change event
            this.changeRack();
          }
        });
      },
      selectShaper() {
        // Open dialog to select shaper and listen for assignment event.
        // PLACEHOLDER {UX} consider preventing assignment if tool is incompatible with reference tooth data

        this.$store.commit('SELECT_SHAPER');

        EventBus.$once('assign-shaper', shaper => {
          // if specified, assign shaper params to form fields
          if (shaper) {
            const geom = shaper.geom;
            this.formGeneratingPinion.pressureAngleNormal = geom.pressureAngleNormal;
            this.formGeneratingPinion.numberOfTeeth = geom.numberOfTeeth;
            this.formGeneratingPinion.addendumCoefficient = geom.addendumCoefficient;
            this.formGeneratingPinion.dedendumCoefficient = geom.dedendumCoefficient;
            this.formGeneratingPinion.profileShiftCoefficient = geom.profileShiftCoefficient;
            this.formGeneratingPinion.protuberanceSize = geom.protuberanceSize;
            this.formGeneratingPinion.tipRadiusCoefficient = geom.tipRadiusCoefficient;
            this.formGeneratingPinion.tipRadiusMax = geom.tipRadiusMax;

            // emit change event
            this.changePinion();
          }
        });
      },
      // VALIDATION
      async validateRack() {
        // Validate generating rack form.
        // If issues arise with using form validate() method, look at other cases using validateField().

        await this.$refs.formGeneratingRack.validate((valid) => {
          if (!valid) {
            throw 'Invalid generating rack'
          }
        });
      },
      async validatePinion() {
        // Validate generating pinion form.
        // If issues arise with using form validate() method, look at other cases using validateField().

        await this.$refs.formGeneratingPinion.validate((valid) => {
          if (!valid) {
            throw 'Invalid generating pinion'
          }
        });
      },
      async validate() {
        // valid tooth form
        try {
          await this.$refs.form.validate();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(e);
          throw 'Invalid tooth form'
        }

        // validate tool form
        if (this.isGeneratingRack) {
          await this.validateRack();
        } else if (this.isGeneratingPinion) {
          await this.validatePinion();
        }

        return true;
      },
      // OTHER
      resetFields() {
        this.$refs.form.resetFields();
      }
    }
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped lang="scss" src="../../assets/css/viewer-tab.scss"/>
<style scoped lang="scss">
    .cutter-btn {
        font-size: 1rem;

        h5 {
            display: inline;
            margin-left: .5em;
        }
    }
    #root-fillet-slider {
        height: 0;  /*removes excess padding*/

        /*TUNING*/
        /*background: coral;*/
    }
</style>
